/**
 * K-MRS 약사 접근코드 절 — 표시 문구·입력 검증(순수 함수).
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — 화면 설계 A 5·7, D9
 *
 * 검증은 돌봄 서버 규칙(D9 「검증」)과 맞춘다. 서버가 최종 판정하고, 여기서는 보내기 전에 같은
 * 규칙으로 [발급]을 막아 사유를 입력 옆에 보여준다.
 */
import type { AdminAccessCode } from "api/kmrsAdminService";

// 안내·경고 문구 ------------------------------------------------------------

/** 「전체 기관」을 고르면 고정되는 경고(화면 설계 A, 사고 재구성 ①2) */
export const ALL_SCOPE_WARNING = "이 코드는 모든 기관 참여자의 이름·연락처를 엽니다";
/**
 * 발급 직후 원문 창 안내(D7a 개정 2026-10-16). 원문을 한 번만 보여 주던 「한 번만 보입니다」를 뒤집어,
 * 이제 목록에서 [복사]·[보기]로 언제든 다시 열람할 수 있음을 알린다. 창 동작(배경·ESC로 안 닫힘)은 유지.
 */
export const CODE_REVEAL_NOTICE = "목록에서 언제든 다시 복사할 수 있어요";

// 길이 제한(화면 설계 A 5, D9) ----------------------------------------------

export const LABEL_MAX = 50;
export const ORG_ID_MAX = 32;
export const ORG_NAME_MAX = 40;
export const ORG_ID_RE = /^[a-z0-9-]{1,32}$/;

// 표시 규칙(화면 설계 A 7) --------------------------------------------------

/** 이름표 NULL → 「(이름표 없음)」 */
export const labelText = (label: string | null): string => label ?? "(이름표 없음)";

/** 범위 — all이면 「전체 기관」, org면 기관 이름(없으면 id) */
export const scopeText = (code: Pick<AdminAccessCode, "scope" | "organization">): string => {
  if (code.scope === "all") return "전체 기관";
  return code.organization?.name ?? code.organization?.id ?? "기관 없음";
};

/** 발급자/폐기자 NULL → 「기록 없음」, 'cli' → 「명령줄」, 그 밖은 그대로(이메일) */
export const actorText = (who: string | null): string => {
  if (!who) return "기록 없음";
  if (who === "cli") return "명령줄";
  return who;
};

// 입력 검증 -----------------------------------------------------------------

export interface CheckResult {
  ok: boolean;
  message?: string;
}

const OK: CheckResult = { ok: true };

/** 이름표 trim 1~50(D9) */
export const validateCodeLabel = (label: string): CheckResult => {
  const v = label.trim();
  if (v.length === 0) return { ok: false, message: "이름표를 입력하세요." };
  if (v.length > LABEL_MAX) return { ok: false, message: `이름표는 ${LABEL_MAX}자까지 쓸 수 있어요.` };
  return OK;
};

/** 새 기관 ID — 영문 소문자·숫자·하이픈 1~32자(D9) */
export const validateNewOrgId = (id: string): CheckResult => {
  const v = id.trim();
  if (v.length === 0) return { ok: false, message: "기관 ID를 입력하세요." };
  if (!ORG_ID_RE.test(v)) return { ok: false, message: "영문 소문자·숫자·하이픈 1~32자로 입력하세요." };
  return OK;
};

/** 새 기관 표시 이름 — trim 0~40, 비우면 ID를 쓴다(D9) */
export const validateNewOrgName = (name: string): CheckResult => {
  if (name.trim().length > ORG_NAME_MAX) return { ok: false, message: `표시 이름은 ${ORG_NAME_MAX}자까지 쓸 수 있어요.` };
  return OK;
};
